// ProfileByHandlePage.tsx — other-user profile, route /profile/:handle.
//
// Not-found-flash avoidance: useUserByHandle's `loading` flag (see
// hooks/useUser.ts) tracks whether the by-handle lookup has actually settled
// for the CURRENT handle — it compares the handle the lookup last resolved
// for against the handle we're rendering now, the same pattern useCommunity
// uses in hooks/useCommunities.ts. This used to be approximated with a 0 ms
// setTimeout race (assuming the lookup was a Promise microtask that always
// beat a macrotask timer), which flashed "not found" for real users whenever
// the lookup was an actual network fetch slower than one timer tick. Gating
// on the hook's own `loading` instead of a timing guess fixes that, and also
// makes handle-to-handle navigation on this same page instance safe without
// a separate stale-render guard: `user` only reflects the current handle
// once `loading` is false.

import { Navigate, useParams } from 'react-router-dom';
import { useSession, useUserByHandle } from '@/hooks';
import { ProfileNotFound } from './ProfileNotFound';
import { ProfileView } from './ProfileView';

export function ProfileByHandlePage() {
  const { handle } = useParams();
  const { currentUser } = useSession();
  const { user, loading } = useUserByHandle(handle ?? null);

  // Own handle → redirect to /profile so auth-gate and canonical URL are consistent.
  // (currentUser is User | null — the undefined check is redundant and removed.)
  if (
    user !== null &&
    currentUser !== null &&
    user.handle.toLowerCase() === currentUser.handle.toLowerCase()
  ) {
    return <Navigate to="/profile" replace />;
  }

  // Still loading — lookup has not yet settled for the current handle.
  // Render neither the profile nor "not found" while this is true.
  if (loading) {
    return null;
  }

  // Handle not found after lookup settled.
  if (user === null) {
    return <ProfileNotFound />;
  }

  // Valid other-user profile.
  return <ProfileView user={user} isOwn={false} />;
}
